class AppointmentService {
  static async saveAppointment(appointmentData) {
    const appointments = this.getAppointments();
    const newAppointment = {
      id: Date.now(),
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      status: 'scheduled', // scheduled, completed, cancelled, no-show
      ...appointmentData
    };
    appointments.push(newAppointment);
    localStorage.setItem('appointments', JSON.stringify(appointments));
    return newAppointment;
  }

  static getAppointments() {
    return JSON.parse(localStorage.getItem('appointments') || '[]');
  }

  static getAppointmentById(id) {
    const appointments = this.getAppointments();
    return appointments.find(apt => apt.id === id);
  }

  static async updateAppointment(id, updateData) {
    const appointments = this.getAppointments();
    const index = appointments.findIndex(apt => apt.id === id);
    if (index !== -1) {
      appointments[index] = {
        ...appointments[index],
        ...updateData,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem('appointments', JSON.stringify(appointments));
      return appointments[index];
    }
    return null;
  }

  static async deleteAppointment(id) {
    const appointments = this.getAppointments();
    const filteredAppointments = appointments.filter(apt => apt.id !== id);
    localStorage.setItem('appointments', JSON.stringify(filteredAppointments));
  }

  // Get appointments for a specific date range
  static getAppointmentsByDateRange(startDate, endDate) {
    const appointments = this.getAppointments();
    return appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= startDate && aptDate <= endDate;
    });
  }

  // Get today's appointments
  static getTodayAppointments() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return this.getAppointmentsByDateRange(today, tomorrow);
  }

  // Get upcoming appointments
  static getUpcomingAppointments() {
    const now = new Date();
    const appointments = this.getAppointments();
    return appointments.filter(apt => new Date(apt.date) > now)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  // Get past appointments
  static getPastAppointments() {
    const now = new Date();
    const appointments = this.getAppointments();
    return appointments.filter(apt => new Date(apt.date) < now)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  // Convert booking to appointment
  static async convertBookingToAppointment(bookingData) {
    // First save or update patient info
    const patientData = {
      name: bookingData.name,
      email: bookingData.email,
      phone: bookingData.phone,
      // Add any other relevant patient data from booking
    };

    // Save appointment
    const appointmentData = {
      patientId: bookingData.patientId,
      date: bookingData.appointmentDate,
      time: bookingData.appointmentTime,
      duration: 30, // default duration in minutes
      type: bookingData.appointmentType || 'consultation',
      notes: bookingData.notes,
      status: 'scheduled'
    };

    return await this.saveAppointment(appointmentData);
  }

  // Get feedback statistics
  static async getFeedbackStats() {
    try {
      // Get all appointments that have feedback
      const appointments = this.getAppointments();
      const appointmentsWithFeedback = appointments.filter(apt => apt.feedback && apt.feedback.rating);
      
      // If no feedback exists, return default values
      if (appointmentsWithFeedback.length === 0) {
        return {
          averageRating: 4.8,
          totalFeedback: 0,
          ratingDistribution: {
            5: 0,
            4: 0,
            3: 0,
            2: 0,
            1: 0
          }
        };
      }
      
      // Calculate average rating
      let totalRating = 0;
      const ratingDistribution = {
        5: 0,
        4: 0,
        3: 0,
        2: 0,
        1: 0
      };
      
      appointmentsWithFeedback.forEach(apt => {
        const rating = parseInt(apt.feedback.rating);
        totalRating += rating;
        
        // Count ratings distribution
        if (rating >= 1 && rating <= 5) {
          ratingDistribution[rating]++;
        }
      });
      
      const averageRating = totalRating / appointmentsWithFeedback.length;
      
      return {
        averageRating,
        totalFeedback: appointmentsWithFeedback.length,
        ratingDistribution
      };
    } catch (error) {
      console.error('Error calculating feedback stats:', error);
      // Return default values in case of error
      return {
        averageRating: 4.8,
        totalFeedback: 0,
        ratingDistribution: {
          5: 0,
          4: 0,
          3: 0,
          2: 0,
          1: 0
        }
      };
    }
  }
}

export default AppointmentService;
